import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useClubs } from '../clubs/useClubs'
import { useAvailability } from './useAvailability'

import {
  AvailableStart,
  BookingDraft,
  ClubAvailability,
  CourtAvailability,
  DayAvailability,
  SelectedSession,
} from '../../types/booking/booking'
import { ClubModelMetadata } from '../../types/court/court'
import { formatDate } from '../../utils/dateUtils'
import { overlaps, sessionKey, sortSessions } from '../../utils/booking/booking'

export type SessionPickerDraft = Pick<BookingDraft, 'clubId' | 'durationMinutes' | 'day' | 'sessions'>

/** Availability served from memory without asking the server again (re-validated in the background once older). */
const FRESH_MS = 20_000
const availabilityCache = new Map<string, { data: ClubAvailability; at: number }>()
/** Forgets cached availability (after booking, the intervals just taken must not look free). */
export const clearAvailabilityCache = () => availabilityCache.clear()

const cacheKey = (clubId: number, date: string, rescheduledBookingId?: number) =>
  `${clubId}|${date}|${rescheduledBookingId ?? ''}`

const nextDay = (day: Date) => {
  const next = new Date(day)
  next.setDate(next.getDate() + 1)
  return next
}

type UseSessionPickerOptions = {
  initial?: SessionPickerDraft
  /** Restrict to one club (e.g. guest links). */
  clubId?: number
  enabled?: boolean
  /** Only one session can be picked (rescheduling); picking another replaces it. */
  single?: boolean
  /** Booking being rescheduled: its own interval is offered, so the move can overlap it. */
  rescheduledBookingId?: number
}

/**
 * Duration-first booking selection: the customer picks a club and a session duration, then a day, and the server
 * returns the start times still bookable for that duration (computed dynamically, half-open intervals). Several
 * sessions can be picked (booked all-or-nothing). On the same court, starts inside a picked session are shown as part
 * of it and earlier starts that would run into it are disabled. One availability response per day covers every session
 * length, so changing the length is instant; days are cached briefly and the next day is
 * prefetched, so switching day rarely waits for the server.
 */
export const useSessionPicker = ({
  initial,
  clubId: fixedClubId,
  enabled = true,
  single = false,
  rescheduledBookingId,
}: UseSessionPickerOptions = {}) => {
  const clubsHook = useClubs()
  const availabilityHook = useAvailability()
  const prefetchHook = useAvailability()
  const [clubs, setClubs] = useState<ClubModelMetadata[]>([])
  const [clubsLoaded, setClubsLoaded] = useState(false)
  const [clubId, setClubIdState] = useState<number | undefined>(fixedClubId ?? initial?.clubId)
  const [durationMinutes, setDurationMinutes] = useState<number | undefined>(
    initial?.durationMinutes
  )
  const [day, setDay] = useState<Date>(() => (initial?.day ? new Date(initial.day) : new Date()))
  const [dayAvailability, setDayAvailability] = useState<ClubAvailability | null>(null)
  const [selected, setSelected] = useState<SelectedSession[]>(initial?.sessions ?? [])
  const [reloadToken, setReloadToken] = useState(0)
  const requestRef = useRef(0)

  useEffect(() => {
    let active = true
    const load = async () => {
      const page = await clubsHook.clubs(50)
      if (!active) {
        return
      }
      const list = page?.content ?? []
      setClubs(list)
      setClubsLoaded(true)
      setClubIdState(current =>
        current !== undefined && list.some(club => club.id === current) ? current : list[0]?.id
      )
    }
    load()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const club = useMemo(() => clubs.find(item => item.id === clubId), [clubs, clubId])

  const durations = useMemo(
    () => [...(club?.sessionDurations ?? [])].sort((a, b) => a - b),
    [club]
  )

  useEffect(() => {
    if (durations.length === 0) {
      return
    }
    setDurationMinutes(current =>
      current !== undefined && durations.includes(current) ? current : durations[0]
    )
  }, [durations])

  // One response per club and day covers every session length: changing the length never asks the server.
  useEffect(() => {
    if (!enabled || clubId === undefined) {
      setDayAvailability(null)
      return
    }
    const date = formatDate(day)
    if (!date) {
      return
    }
    const request = ++requestRef.current
    const key = cacheKey(clubId, date, rescheduledBookingId)
    const cached = availabilityCache.get(key)
    setDayAvailability(cached?.data ?? null)

    const prefetchNextDay = () => {
      const following = formatDate(nextDay(day))
      if (!following || availabilityCache.has(cacheKey(clubId, following, rescheduledBookingId))) {
        return
      }
      prefetchHook
        .availability(clubId, following, rescheduledBookingId)
        .then(result => {
          if (result) {
            availabilityCache.set(cacheKey(clubId, following, rescheduledBookingId), { data: result, at: Date.now() })
          }
        })
        .catch(() => {
          // best effort
        })
    }

    if (cached && Date.now() - cached.at < FRESH_MS) {
      prefetchNextDay()
      return
    }
    availabilityHook
      .availability(clubId, date, rescheduledBookingId)
      .then(result => {
        if (result) {
          availabilityCache.set(key, { data: result, at: Date.now() })
        }
        if (request === requestRef.current) {
          setDayAvailability(result ?? null)
          prefetchNextDay()
        }
      })
      .catch(() => {
        // error is exposed through availabilityHook.error
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, day, enabled, reloadToken])

  const availability: DayAvailability | null = useMemo(() => {
    const session = dayAvailability?.sessions.find(item => item.durationMinutes === durationMinutes)
    return dayAvailability && session
      ? { ...session, incrementMinutes: dayAvailability.incrementMinutes }
      : null
  }, [dayAvailability, durationMinutes])

  const setClubId = useCallback(
    (next: number) => {
      if (next !== clubId) {
        // One checkout books one club.
        setSelected([])
        setClubIdState(next)
      }
    },
    [clubId]
  )

  const selectedKeys = useMemo(() => new Set(selected.map(sessionKey)), [selected])

  const isSelected = useCallback(
    (courtId: number, start: AvailableStart) =>
      selectedKeys.has(sessionKey({ courtId, startTime: start.startTime })),
    [selectedKeys]
  )

  /** A start inside a session picked on the same court (after its start): part of that reservation. */
  const isCovered = useCallback(
    (courtId: number, start: Pick<AvailableStart, 'startTime'>) =>
      selected.some(
        session =>
          session.courtId === courtId &&
          session.startTime < start.startTime &&
          start.startTime < session.endTime
      ),
    [selected]
  )

  /** An earlier start whose session would run into a session picked on the same court. */
  const isBlocked = useCallback(
    (courtId: number, start: AvailableStart) =>
      !single &&
      selected.some(
        session =>
          session.courtId === courtId &&
          start.startTime < session.startTime &&
          overlaps(session, start)
      ),
    [selected, single]
  )

  const toggle = useCallback(
    (court: CourtAvailability, start: AvailableStart) => {
      if (durationMinutes === undefined) {
        return
      }
      const key = sessionKey({ courtId: court.courtId, startTime: start.startTime })
      setSelected(current => {
        if (current.some(session => sessionKey(session) === key)) {
          return current.filter(session => sessionKey(session) !== key)
        }
        if (
          !single &&
          current.some(
            session => session.courtId === court.courtId && overlaps(session, start)
          )
        ) {
          return current
        }
        return sortSessions([
          ...(single ? [] : current),
          {
            courtId: court.courtId,
            courtName: court.courtName,
            startTime: start.startTime,
            endTime: start.endTime,
            durationMinutes,
            price: start.price,
          },
        ])
      })
    },
    [durationMinutes, single]
  )

  const remove = useCallback((session: SelectedSession) => {
    setSelected(current => current.filter(item => sessionKey(item) !== sessionKey(session)))
  }, [])

  const clear = useCallback(() => setSelected([]), [])

  /** Re-reads availability (e.g. after a conflict: someone else took the interval). */
  const reload = useCallback(() => {
    clearAvailabilityCache()
    setReloadToken(token => token + 1)
  }, [])

  const draft: SessionPickerDraft = useMemo(
    () => ({
      clubId,
      durationMinutes,
      day: day.toISOString(),
      sessions: selected,
    }),
    [clubId, durationMinutes, day, selected]
  )

  return {
    clubs,
    clubsLoaded,
    club,
    clubId,
    setClubId,
    durations,
    durationMinutes,
    setDurationMinutes,
    day,
    setDay,
    availability,
    availabilityLoading: availabilityHook.loading || (enabled && clubsLoaded && !availability && clubId !== undefined),
    error: clubsHook.error || availabilityHook.error,
    resetError: () => {
      clubsHook.resetState()
      availabilityHook.resetState()
    },
    single,
    selected,
    isSelected,
    isCovered,
    isBlocked,
    toggle,
    remove,
    clear,
    reload,
    draft,
  }
}

export type SessionPicker = ReturnType<typeof useSessionPicker>
